/**
 * Route utilities for generating SEO-friendly URLs
 */

/** Query param used to deep-link to a specific comment from notifications */
export const FEATURED_COMMENT_PARAM = 'lc';

/**
 * Sanitizes a string for use in URLs
 * - Converts to lowercase
 * - Replaces spaces and special characters with hyphens
 * - Removes consecutive hyphens
 * - Trims hyphens from start/end
 */
export function slugify(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '') // Remove special characters
    .replace(/[\s_]+/g, '-') // Replace spaces/underscores with hyphens
    .replace(/-+/g, '-') // Remove consecutive hyphens
    .replace(/^-|-$/g, ''); // Trim hyphens from ends
}

/**
 * Generates an SEO-friendly series route
 * Format: /series/{id}-{slugified-title}
 * Fallback: /series/{id} if title is missing
 */
export function getSeriesRoute(id: string | number, title?: string): string {
  const baseId = String(id);
  if (!title?.trim()) {
    return `/series/${baseId}`;
  }
  const slug = slugify(title);
  return slug ? `/series/${baseId}-${slug}` : `/series/${baseId}`;
}

/**
 * Generates an SEO-friendly series play route
 * Format: /series/{id}-{slugified-title}/play/{videoUuid}
 */
export function getSeriesPlayRoute(
  id: string | number,
  title: string | undefined,
  videoUuid: string
): string {
  const baseId = String(id);
  if (!title?.trim()) {
    return `/series/${baseId}/play/${videoUuid}`;
  }
  const slug = slugify(title);
  return slug
    ? `/series/${baseId}-${slug}/play/${videoUuid}`
    : `/series/${baseId}/play/${videoUuid}`;
}

/**
 * Extracts the numeric ID from a hybrid slug
 * Input: "15-breaking-bad" → Output: "15"
 * Input: "15" → Output: "15"
 */
export function extractIdFromSlug(slug: string): string {
  const parts = slug.split('-');
  return parts[0] || slug;
}

/**
 * Validates that a string represents a valid numeric ID
 */
export function isValidId(id: string): boolean {
  const num = parseInt(id, 10);
  return !isNaN(num) && num > 0;
}

/**
 * Generate creator profile route using handler
 * @param handler - Creator's handler/username (e.g., "arab", "johndoe")
 * @returns Route string for creator profile
 */
export function getCreatorRoute(handler: string | undefined | null): string {
  if (!handler) {
    return '/creators';
  }
  return `/${handler}`;
}

/**
 * Resolve the creator username off a channel payload. BE's canonical key is
 * `handle` (newer endpoints emit only it); `handler` is a legacy typo the
 * older endpoints still emit (FRONT-245). Drop the fallback once BE removes
 * the legacy key.
 */
export function getChannelHandle(
  channel: { handle?: string | null; handler?: string | null } | null | undefined
): string | undefined {
  return channel?.handle ?? channel?.handler ?? undefined;
}

/**
 * Minimal shape accepted by course/bundle URL helpers.
 * Accepts either a full object or just the fields required to build the URL,
 * so callers don't have to hydrate the whole entity just to render a link.
 * `slug` is `string | null | undefined` so callers can pass DB rows where the
 * column is null, or partial objects that omit the field entirely, without
 * needing conditional spread under `exactOptionalPropertyTypes`.
 */
interface Sluggable {
  id: string | number;
  slug?: string | null | undefined;
}

/**
 * Generate a course detail route. Uses the human-readable slug when present,
 * falls back to the numeric id.
 * Backend accepts either form at GET /api/courses/{identifier}.
 */
export function getCourseRoute(course: Sluggable): string {
  return `/courses/${course.slug ?? course.id}`;
}

/**
 * Generate a course bundle detail route. Uses slug when present, falls back to id.
 */
export function getBundleRoute(bundle: Sluggable): string {
  return `/courses/bundles/${bundle.slug ?? bundle.id}`;
}

/**
 * Generate a lesson player route. Uses the course slug when present for the
 * course segment; the lesson is always identified by its UUID.
 */
export function getCoursePlayRoute(course: Sluggable, videoUuid: string): string {
  return `/courses/${course.slug ?? course.id}/play/${videoUuid}`;
}

/**
 * Per-creator promo override map for the choose-plan CTA.
 *
 * TEMPORARY: Justin (handle `justinalvo`) gets `?promo=justinalvo` auto-applied
 * on every /choose-plan link rendered from his channel surfaces. Other creators
 * are unaffected. Drop this map (and the helper below) once the backend
 * promo-code scope lookup ships — see `docs/backend-requests/README.md` → S2.
 */
const CREATOR_PROMO_CODES: Readonly<Record<string, string>> = {
  justinalvo: 'justinalvo',
};

/**
 * Build the `/choose-plan` href with the current creator's promo applied (when
 * mapped above). Pass the route's `[handler]` param. Outside creator surfaces,
 * keep using the bare `'/choose-plan'` literal.
 */
export function getChoosePlanHrefForCreator(handler: string | undefined | null): string {
  if (!handler) return '/choose-plan';
  const promo = CREATOR_PROMO_CODES[handler];
  return promo ? `/choose-plan?promo=${promo}` : '/choose-plan';
}
